import React from 'react'
// components
import { PopoverModal } from 'components/UI'
import AddEntityForm from '../AddForm/AddEntityForm'
// icons
import AddCircle from '@mui/icons-material/AddCircle'
// context
import { ObjectivesListTreeBLContext } from 'containers/ObjectivesList/layers/BL/Tree'
// constants
import { ENTITIES } from 'constants/index'
import { OBJECT_ENTITY_NAMES } from 'constants/objects'
// themes
import { colors } from 'themes/index'
// types
import { IPlanTypeLevel, IPlanTypeLevelOption } from 'types/graphql'
// views
import { ActionIcon, AddButton, MeasureIcon, MenuItem, MenuWrapper, ObjectiveIcon, Wrapper } from './views'
// utils
import { first, get, orderBy, upperFirst } from 'lodash'

interface ISelectedRootType {
  value: string
  label: string
  level: IPlanTypeLevel
  typeOption: IPlanTypeLevelOption | null
  // Bumped on every (re)open: it keys the form, so it remounts empty and refocused after each
  // created item.
  formKey: number
}

const PlanRootCreate: React.FC = () => {
  const { plan, submitRootCreate } = React.useContext(ObjectivesListTreeBLContext)

  const [selected, setSelected] = React.useState<ISelectedRootType | null>(null)

  const formKeyRef = React.useRef(0)

  const levels: IPlanTypeLevel[] = orderBy(get(plan, 'planType.levels', []), 'position', 'asc')
  const rootLevel: IPlanTypeLevel = first(levels) || ({} as IPlanTypeLevel)
  const typeOptions: IPlanTypeLevelOption[] = rootLevel.typeOptions || []

  const { planAddObjective, planAddAction, planAddKPI } = get<any, any, any>(plan, 'currentPermissions', {})

  const canAdd = planAddObjective || planAddAction || planAddKPI

  if (plan.locked || !canAdd) {
    return null
  }

  const getNextFormKey = () => {
    formKeyRef.current += 1

    return formKeyRef.current
  }

  // Opens the inline create-form for the chosen root type.
  const openCreateForm =
    (value: string, label: string, typeOption: IPlanTypeLevelOption | null = null) =>
    () =>
      setSelected({ value, label, level: rootLevel, typeOption, formKey: getNextFormKey() })

  const handleSubmit = async ({ name }: { name: string }) => {
    const createdPlanItem = await submitRootCreate({
      name,
      rootType: { value: selected.value, typeOption: selected.typeOption }
    })

    if (!createdPlanItem) {
      return
    }

    // Keep the form open so several root items can be added in a row — a new key remounts it empty.
    // `prev` stays null when the user closed the form while the mutation was in flight.
    setSelected((prev) => (prev ? { ...prev, formKey: getNextFormKey() } : prev))
  }

  const handleClose = () => setSelected(null)

  if (selected) {
    return (
      <Wrapper>
        <AddEntityForm
          key={selected.formKey}
          level={selected.level}
          form={`CREATE_ROOT_${selected.value}_${selected.formKey}`}
          label={`Enter a description for this ${selected.label}`}
          onSubmit={handleSubmit}
          onClose={handleClose}
        />
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <PopoverModal
        trigger={
          <AddButton>
            <AddCircle htmlColor={colors.blue500} />
            <span>Add item</span>
          </AddButton>
        }>
        {({ closePopup }) => {
          // Menu item click: open the create-form for the type, then close the popover.
          const handleTypeSelect =
            (value: string, label: string, typeOption?: IPlanTypeLevelOption) => (event: React.MouseEvent) => {
              openCreateForm(value, label, typeOption || null)()
              closePopup(event)
            }

          return (
            <MenuWrapper>
              {planAddObjective && (
                <>
                  <MenuItem
                    onClick={handleTypeSelect(
                      ENTITIES.PLAN_OBJECTIVE,
                      rootLevel.description || OBJECT_ENTITY_NAMES.OBJECTIVE
                    )}>
                    <ObjectiveIcon />
                    {upperFirst(rootLevel.description || OBJECT_ENTITY_NAMES.OBJECTIVE)}
                  </MenuItem>
                  {typeOptions.map((typeOption) => (
                    <MenuItem
                      key={typeOption.id}
                      onClick={handleTypeSelect(ENTITIES.PLAN_OBJECTIVE, typeOption.label, typeOption)}>
                      <ObjectiveIcon />
                      {upperFirst(typeOption.label)}
                    </MenuItem>
                  ))}
                </>
              )}
              {planAddAction && (
                <MenuItem onClick={handleTypeSelect(ENTITIES.PLAN_ACTION, OBJECT_ENTITY_NAMES.ACTION)}>
                  <ActionIcon />
                  {upperFirst(OBJECT_ENTITY_NAMES.ACTION)}
                </MenuItem>
              )}
              {planAddKPI && (
                <MenuItem onClick={handleTypeSelect(ENTITIES.PLAN_KPI, OBJECT_ENTITY_NAMES.KPI)}>
                  <MeasureIcon />
                  {upperFirst(OBJECT_ENTITY_NAMES.KPI)}
                </MenuItem>
              )}
            </MenuWrapper>
          )
        }}
      </PopoverModal>
    </Wrapper>
  )
}

export default PlanRootCreate
